import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import {
  ApiService,
  CreatePlatformOrganizationPayload,
  PlatformCompanyAdminItem,
  PlatformOrganizationItem,
  UpdatePlatformOrganizationPayload,
} from '../../shared/services/api.service';
import { CreatePlatformOrganizationDialogComponent } from './create-platform-organization-dialog.component';
import { IdDocumentPreviewDialogComponent } from '../admin/users/id-document-preview-dialog.component';
import { EditPlatformOrganizationDialogComponent } from './edit-platform-organization-dialog.component';
import { SmallLoadingDialogComponent } from '../../shared/components/small-loading-dialog/small-loading-dialog.component';

@Component({
  selector: 'app-platform-admin',
  templateUrl: './platform-admin.component.html',
  styleUrls: ['./platform-admin.component.css'],
})
export class PlatformAdminComponent implements OnInit {
  loading = false;
  organizations: PlatformOrganizationItem[] = [];
  companyAdmins: PlatformCompanyAdminItem[] = [];

  private readonly defaultCreateOrgPayload = {
    displayName: '',
    primaryColor: '#1e88e5',
    secondaryColor: '#00A3E0',
    logoUrl: '',
    mailLogoUrl: '',
    initialAdminEmails: '',
    billingOwnerEmail: '',
    billingPaymentType: 'CARD' as 'CARD' | 'INVOICE',
  };
  createOrgPayload = { ...this.defaultCreateOrgPayload };

  constructor(
    private readonly apiService: ApiService,
    private readonly dialog: MatDialog,
    private readonly snackBar: MatSnackBar,
    private readonly router: Router
  ) {}

  ngOnInit(): void {
    this.refresh();
  }

  refresh(): void {
    this.loading = true;
    this.apiService.listPlatformOrganizations().subscribe({
      next: (response) => {
        this.organizations = response.organizations || [];
        this.apiService.listPlatformCompanyAdmins().subscribe({
          next: (adminsResponse) => {
            this.companyAdmins = adminsResponse.companyAdmins || [];
            this.loading = false;
          },
          error: (error) => {
            this.loading = false;
            const message = error?.error?.error?.message || 'Failed to load company admins';
            this.snackBar.open(message, 'Close', { duration: 5000 });
          },
        });
      },
      error: (error) => {
        this.loading = false;
        const message = error?.error?.error?.message || 'Failed to load organizations';
        this.snackBar.open(message, 'Close', { duration: 5000 });
      },
    });
  }

  approve(membershipId: string): void {
    this.apiService.approvePlatformCompanyAdmin(membershipId).subscribe({
      next: () => {
        this.snackBar.open('Company admin approved', 'Close', { duration: 4000 });
        this.refresh();
      },
      error: (error) => {
        const message = error?.error?.error?.message || 'Failed to approve company admin';
        this.snackBar.open(message, 'Close', { duration: 5000 });
      },
    });
  }

  reject(membershipId: string): void {
    this.apiService.rejectPlatformCompanyAdmin(membershipId).subscribe({
      next: () => {
        this.snackBar.open('Company admin rejected', 'Close', { duration: 4000 });
        this.refresh();
      },
      error: (error) => {
        const message = error?.error?.error?.message || 'Failed to reject company admin';
        this.snackBar.open(message, 'Close', { duration: 5000 });
      },
    });
  }

  viewCompanyAdminIdDocument(admin: PlatformCompanyAdminItem): void {
    this.apiService.getUserIdDocumentUrl(admin.userId, admin.orgId).subscribe({
      next: (response) => {
        this.dialog.open(IdDocumentPreviewDialogComponent, {
          width: '90vw',
          maxWidth: '1100px',
          data: {
            userEmail: admin.email || null,
            phone: response.phone || admin.phone || null,
            industry: response.industry || admin.industry || null,
            industryOther: response.industryOther || admin.industryOther || null,
            ffcDocumentPath: response.ffcDocumentPath || admin.ffcDocumentPath || null,
            ffcSignedUrl: response.ffcSignedUrl || null,
            ffcExpiresAt: response.ffcExpiresAt || null,
            idDocumentPath: response.idDocumentPath || admin.idDocumentPath || null,
            signedUrl: response.signedUrl,
            expiresAt: response.expiresAt,
          },
        });
      },
      error: (error) => {
        const message = error?.error?.error?.message || 'Failed to load ID document preview';
        this.snackBar.open(message, 'Close', { duration: 5000 });
      },
    });
  }

  resetCompanyAdminPassword(admin: PlatformCompanyAdminItem): void {
    if (!admin.email) return;
    if (!confirm(`Send password reset email to ${admin.email}?`)) return;
    this.apiService.resetUserPassword(admin.userId, admin.orgId).subscribe({
      next: () => {
        this.snackBar.open(`Password reset email sent to ${admin.email}`, 'Close', { duration: 5000 });
      },
      error: (error) => {
        const message = error?.error?.error?.message || 'Failed to send password reset email';
        this.snackBar.open(message, 'Close', { duration: 5000 });
      },
    });
  }

  resendCompanyAdminInvite(admin: PlatformCompanyAdminItem): void {
    if (!admin.email) return;
    if (!confirm(`Resend invite email to ${admin.email}?`)) return;
    this.apiService.resendPlatformCompanyAdminInvite(admin.membershipId).subscribe({
      next: () => {
        this.snackBar.open(`Invite email resent to ${admin.email}`, 'Close', { duration: 5000 });
      },
      error: (error) => {
        const message = error?.error?.error?.message || 'Failed to resend invite';
        this.snackBar.open(message, 'Close', { duration: 5000 });
      },
    });
  }

  deleteCompanyAdmin(admin: PlatformCompanyAdminItem): void {
    const adminEmail = admin.email || 'this admin';
    const confirmed = confirm(
      `Delete company admin ${adminEmail} from "${admin.orgDisplayName}"?\n\nThis removes their admin membership for this organization.`
    );
    if (!confirmed) return;
    this.apiService.deletePlatformCompanyAdmin(admin.membershipId).subscribe({
      next: () => {
        this.snackBar.open(`Deleted ${adminEmail} from ${admin.orgDisplayName}`, 'Close', { duration: 5000 });
        this.refresh();
      },
      error: (error) => {
        const message = error?.error?.error?.message || 'Failed to delete company admin';
        this.snackBar.open(message, 'Close', { duration: 6000 });
      },
    });
  }

  openCreateOrganizationDialog(): void {
    const dialogRef = this.dialog.open(CreatePlatformOrganizationDialogComponent, {
      width: '560px',
      data: {
        ...this.createOrgPayload,
      },
    });
    dialogRef.afterClosed().subscribe((payload: CreatePlatformOrganizationPayload | null | undefined) => {
      if (!payload) return;
      this.createOrgPayload = {
        displayName: payload.displayName,
        primaryColor: payload.primaryColor,
        secondaryColor: payload.secondaryColor,
        logoUrl: payload.logoUrl || '',
        mailLogoUrl: payload.mailLogoUrl || '',
        initialAdminEmails: payload.initialAdminEmails.join(', '),
        billingOwnerEmail: payload.billingOwnerEmail,
        billingPaymentType: payload.billingPaymentType,
      };
      this.loading = true;
      this.apiService.createPlatformOrganization(payload).subscribe({
      next: () => {
        this.createOrgPayload = { ...this.defaultCreateOrgPayload };
        this.snackBar.open('Organization created successfully', 'Close', { duration: 4000 });
        this.refresh();
      },
      error: (error) => {
        this.loading = false;
        const message = error?.error?.error?.message || 'Failed to create organization';
        this.snackBar.open(message, 'Close', { duration: 5000 });
      },
    });
    });
  }

  editOrganization(org: PlatformOrganizationItem): void {
    const existingAdmins = Array.from(
      new Map(
        this.companyAdmins
          .filter((admin) => admin.orgId === org.orgId && admin.email)
          .map((admin) => [
            String(admin.email || '').trim().toLowerCase(),
            {
              email: String(admin.email || '').trim().toLowerCase(),
              isBillingOwner: admin.isBillingOwner === true,
            },
          ])
      ).values()
    );
    const dialogRef = this.dialog.open(EditPlatformOrganizationDialogComponent, {
      width: '560px',
      data: {
        organization: org,
        existingAdmins,
      },
    });
    dialogRef.afterClosed().subscribe((payload: UpdatePlatformOrganizationPayload | null | undefined) => {
      if (!payload) return;
      this.loading = true;
      this.apiService.updatePlatformOrganization(org.orgId, payload).subscribe({
        next: () => {
          this.snackBar.open('Organization updated successfully', 'Close', { duration: 4000 });
          this.refresh();
        },
        error: (error) => {
          this.loading = false;
          const message = error?.error?.error?.message || 'Failed to update organization';
          this.snackBar.open(message, 'Close', { duration: 5000 });
        },
      });
    });
  }

  deleteOrganization(org: PlatformOrganizationItem): void {
    const confirmed = confirm(
      `Delete organization "${org.displayName}"?\n\nThis will suspend access, cancel subscriptions, offboard users, and soft-delete the org.`
    );
    if (!confirmed) return;
    const loadingRef = this.dialog.open(SmallLoadingDialogComponent, {
      width: '280px',
      disableClose: true,
      data: { message: 'Deleting organization...' },
      panelClass: 'small-loading-dialog-panel',
    });
    this.loading = true;
    this.apiService.deletePlatformOrganization(org.orgId).subscribe({
      next: (response) => {
        loadingRef.close();
        this.snackBar.open(
          `Organization deleted. Users offboarded: ${response.deletedUsers}, subscriptions cancelled: ${response.cancelledSubscriptions}`,
          'Close',
          { duration: 6000 }
        );
        this.refresh();
      },
      error: (error) => {
        loadingRef.close();
        this.loading = false;
        const message = error?.error?.error?.message || 'Failed to delete organization';
        this.snackBar.open(message, 'Close', { duration: 5000 });
      },
    });
  }

  openOrganizationBilling(org: PlatformOrganizationItem): void {
    void this.router.navigate(['/platform-admin/organizations', org.orgId, 'billing']);
  }
}
